import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetchOverdueBurndown } from "@api";
import { buildApiQuery } from "@utils";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LONG_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Layers by how many months before the current one the invoices fell due; the last folds in everything older.
export const LAYER_COUNT = 5;

const pad = (n) => String(n).padStart(2, "0");
const isoDate = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;
const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

// API dates are "yyyy-MM-dd"; read them as calendar parts so no timezone can shift a day.
const parts = (iso) => {
  const [year, month, day] = iso.split("-").map(Number);
  return { year, month: month - 1, day };
};

const monthsBetween = (from, to) => (to.year - from.year) * 12 + (to.month - from.month);

const shiftMonth = ({ year, month }, by) => {
  const total = year * 12 + month + by;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
};

export const burndownMonthName = (today) => LONG_MONTH_NAMES[today.getMonth()];

export const shortMonthName = (date) => MONTH_NAMES[date.getMonth()];

// Window to fetch: from the first of last month, so this month's bars and the same-day-last-month line come
// from one request.
export const burndownFrom = (today) => {
  const previous = shiftMonth({ year: today.getFullYear(), month: today.getMonth() }, -1);
  return isoDate(previous.year, previous.month, 1);
};

export const burndownLayers = (today) => {
  const current = { year: today.getFullYear(), month: today.getMonth() };
  return Array.from({ length: LAYER_COUNT }, (_, i) => {
    const { month } = shiftMonth(current, -i);
    return { key: `m${i}`, label: i === LAYER_COUNT - 1 ? `${MONTH_NAMES[month]} & earlier` : MONTH_NAMES[month] };
  });
};

const layerIndex = (dueMonth, current) => Math.min(Math.max(monthsBetween(parts(dueMonth), current), 0), LAYER_COUNT - 1);

/**
 * Shapes the burndown days into one row per day of the current month. Days still to come carry no layers
 * (the chart draws them as placeholders); every day carries the overdue balance on the same day last month,
 * or null where last month was shorter.
 */
export const buildBurndown = (days, today) => {
  const current = { year: today.getFullYear(), month: today.getMonth() };
  const previous = shiftMonth(current, -1);
  const todayDay = today.getDate();
  const byDate = new Map((days ?? []).map((d) => [d.date, d]));

  const rows = Array.from({ length: daysInMonth(current.year, current.month) }, (_, i) => {
    const day = i + 1;
    const date = isoDate(current.year, current.month, day);
    const lastMonth =
      day <= daysInMonth(previous.year, previous.month)
        ? byDate.get(isoDate(previous.year, previous.month, day))?.overdue ?? null
        : null;
    const row = { day, date, lastMonth, future: day > todayDay, touchpoints: 0, collected: 0, total: null, movement: null };

    const point = byDate.get(date);
    if (row.future || !point) return row;

    for (let layer = 0; layer < LAYER_COUNT; layer++) row[`m${layer}`] = 0;
    for (const { dueMonth, amount } of point.byDueMonth ?? []) {
      row[`m${layerIndex(dueMonth, current)}`] += amount;
    }
    // The service guarantees opening + fell due − cleared + other = the day's overdue, so opening is derived.
    const movement = {
      fellDue: point.fellDue ?? 0,
      cleared: point.cleared ?? 0,
      otherIncrease: point.otherIncrease ?? 0,
    };
    movement.opening = point.overdue - movement.fellDue + movement.cleared - movement.otherIncrease;
    return {
      ...row,
      total: point.overdue,
      touchpoints: point.paymentRequestsSent ?? 0,
      collected: point.collected ?? 0,
      movement,
    };
  });

  const todayRow = rows[todayDay - 1];
  const latest = todayRow?.total ?? null;
  const sameDayLastMonth = todayRow?.lastMonth ?? null;

  return {
    rows,
    latest,
    change: latest !== null && sameDayLastMonth !== null ? latest - sameDayLastMonth : null,
  };
};

export const useOverdueBurndown = (filters, today) => {
  const query = buildApiQuery({ filters });
  const from = burndownFrom(today);
  return useQuery({
    queryKey: ["overdueBurndown", query, from],
    queryFn: ({ signal }) => fetchOverdueBurndown({ query, from }, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
};
